import { useEffect, useRef, useState } from "react";

/**
 * AnimatedCounter — smoothly counts from the previous value to the new one
 * using rAF + a mild ease-out curve. Used for KPI tiles, progress %, etc.
 *
 * Props:
 *   value    — target number
 *   duration — ms, defaults to 620
 *   format   — optional (n) => string (e.g. n => `${n}%`)
 *   className — passed through
 */
export default function AnimatedCounter({ value = 0, duration = 620, format, className = "", ...rest }) {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const startRef = useRef(0);

  useEffect(() => {
    fromRef.current = display;
    startRef.current = performance.now();
    let raf = 0;
    const tick = (t) => {
      const elapsed = t - startRef.current;
      const p = Math.min(1, elapsed / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - p, 3);
      const v = fromRef.current + (value - fromRef.current) * eased;
      setDisplay(v);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // `display` is intentionally not a dep — we animate FROM its live value
    // via the closure snapshot each tick; adding it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  const rounded = Math.round(display);
  const text = format ? format(rounded) : String(rounded);
  return (
    <span className={`tabular-nums ${className}`} {...rest}>
      {text}
    </span>
  );
}
